import { Toolbar, ToolbarWidget, ToolbarWidgetGroup } from '@angular/aria/toolbar';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { AuthStateService } from '@arya/auth/data-access';
import { PLATFORM_BASE_PATH } from '@arya/shared/feature';

import { NoxoCardOverline } from '@noxo/ui/card';
import { NoxoChip, NoxoChipGroup } from '@noxo/ui/chip-group';
import { NoxoMessage } from '@noxo/ui/message';

import {
  DEMO_INSTALLATION_OPTIONS,
  DEMO_REGISTRATION_OPTIONS,
  DemoInstallation,
  type DemoInstallationId,
  type DemoRegistrationId,
} from './demo-installation';
import { demoPersonas } from './demo-personas';
import { DemoSignIn } from './demo-sign-in';

const NOT_RUNNING = 'not running here: build it with "bun run db:indonesia" and restart "bun run dev"';

@Component({
  selector: 'noxo-demo-installations',
  imports: [NoxoCardOverline, NoxoChip, NoxoChipGroup, NoxoMessage, Toolbar, ToolbarWidget, ToolbarWidgetGroup],
  providers: [DemoInstallation],
  template: `
    <noxo-card-overline>Installation · deployment, entity and rules</noxo-card-overline>
    <noxo-chip-group
      ngToolbar
      ngToolbarWidgetGroup
      role="radiogroup"
      aria-label="Installation">
      @for (installation of installations(); track installation.id) {
        <button
          type="button"
          noxo-chip
          ngToolbarWidget
          role="radio"
          [attr.aria-checked]="installation.active"
          [disabled]="!installation.reachable || busy()"
          [title]="installation.caption"
          (click)="open(installation.id)">
          {{ installation.label }}
        </button>
      }
    </noxo-chip-group>
    @if (registrations().length > 0) {
      <noxo-card-overline>EU residents · who may register</noxo-card-overline>
      <noxo-chip-group
        ngToolbar
        ngToolbarWidgetGroup
        role="radiogroup"
        aria-label="EU residents">
        @for (registration of registrations(); track registration.id) {
          <button
            type="button"
            noxo-chip
            ngToolbarWidget
            role="radio"
            [attr.aria-checked]="registration.active"
            [disabled]="busy()"
            [title]="registration.caption"
            (click)="useRegistration(registration.id)">
            {{ registration.label }}
          </button>
        }
      </noxo-chip-group>
    }
    @if (failed()) {
      <noxo-message type="error">Switching failed.</noxo-message>
    }
  `,
  styleUrl: './demo-chips.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DemoInstallations {
  private readonly installation = inject(DemoInstallation);
  private readonly document = inject(DOCUMENT);
  private readonly signIn = inject(DemoSignIn);
  private readonly currentUser = inject(AuthStateService).currentUser;
  private readonly basePath = inject(PLATFORM_BASE_PATH);

  protected readonly busy = signal(false);
  protected readonly failed = signal(false);
  protected readonly installations = computed(() =>
    DEMO_INSTALLATION_OPTIONS.map((option) => ({
      ...option,
      active: option.id === this.installation.installation(),
      reachable: this.isReachable(option.id),
      caption: this.isReachable(option.id) ? option.caption : `${option.caption} — ${NOT_RUNNING}`,
    }))
  );
  protected readonly registrations = computed(() =>
    this.installation.installation() === 'global'
      ? DEMO_REGISTRATION_OPTIONS.map((option) => ({
          ...option,
          active: option.id === this.installation.registration(),
        }))
      : []
  );

  protected async open(id: DemoInstallationId): Promise<void> {
    if (id === this.installation.installation()) return;
    const url = this.installation.urls()?.[id];
    if (!this.installation.available().includes(id)) {
      if (url !== undefined) this.document.location.assign(url);
      return;
    }
    await this.run(async () => {
      const email = this.currentUser()?.email;
      const { signedIn } = await this.installation.switchTo(id);
      const persona = demoPersonas(this.basePath, 'ecspr').find((candidate) => candidate.email === email);
      if (!signedIn && persona !== undefined) await this.signIn.authenticate(persona);
    });
  }

  protected async useRegistration(id: DemoRegistrationId): Promise<void> {
    await this.run(() => this.installation.useRegistration(id));
  }

  private async run(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.failed.set(false);
    try {
      await action();
      this.document.location.reload();
    } catch {
      this.failed.set(true);
      this.busy.set(false);
    }
  }

  private isReachable(id: DemoInstallationId): boolean {
    return this.installation.available().includes(id) || this.installation.urls()?.[id] !== undefined;
  }
}
